import { KeyboardEvent, MouseEvent as ReactMouseEvent } from 'react';
import Switch from 'react-switch';
import { AlertTriangle, Check, CheckCircle2, Equal, MousePointerClick, RotateCcw, X } from 'lucide-react';
import { chartTokens } from '../../design-system/chartTokens';
import type { BDTheme } from '../../design-system/types';
import './cvssThresholdConfig.scss';
import { useTranslation } from 'react-i18next';

type Level = 'low' | 'medium' | 'high' | 'critical';

type Props = {
  theme: BDTheme;
  thresholds: Record<Level, number>;
  setThreshold: Record<Level, (value: number) => void>;
  enabled: Record<Level, boolean>;
  setEnabled: Record<Level, (value: boolean) => void>;
  dragging: string | null;
  onDragStart: (threshold: string, clientX: number) => void;
  onDragEnd: () => void;
  applyThreshold: (threshold: string, value: number) => void;
  containerRef: HTMLDivElement | null;
  setContainerRef: (el: HTMLDivElement | null) => void;
  valueFromPosition: (clientX: number, container: HTMLDivElement) => number;
  coverage: string;
  onApply: () => void;
  onDiscard: () => void;
  onReset: () => void;
};

// Severity colours; critical has no design-system token, so it gets a crimson per theme
const criticalColor: Record<BDTheme, string> = { light: '#a3123f', dark: '#ff7aa8' };

// Display texts (name, badge letter, description) come from settings.cvss.levels.<key>
const LEVELS: { key: Level }[] = [{ key: 'low' }, { key: 'medium' }, { key: 'high' }, { key: 'critical' }];

const pct = (value: number) => `${(value / 10) * 100}%`;

export const CvssThresholdConfig = (props: Props) => {
  const { t: translate } = useTranslation();
  const { thresholds: t, enabled } = props;
  const t2 = chartTokens[props.theme];
  const levelName = (key: Level) => translate(`settings.cvss.levels.${key}.name`);
  const switchColor: Record<Level, string> = {
    low: t2.accent,
    medium: t2.warn,
    high: t2.danger,
    critical: criticalColor[props.theme],
  };

  // Range of each severity band on the 0–10 scale
  const bands: Record<Level, [number, number]> = {
    low: [0, t.low],
    medium: [t.low, t.medium],
    high: [t.medium, t.high],
    critical: [t.high, t.critical],
  };

  const activeLevels = LEVELS.filter((level) => enabled[level.key]).length;
  const wellOrdered = t.low < t.medium && t.medium < t.high && t.high < t.critical;

  const presets = [
    { id: 'nist', label: translate('settings.cvss.presets.nist'), icon: RotateCcw, values: [3.9, 6.9, 8.9, 10.0] },
    { id: 'equal', label: translate('settings.cvss.presets.equal'), icon: Equal, values: [2.5, 5.0, 7.5, 10.0] },
  ];
  const applyPreset = ([low, medium, high, critical]: number[]) => {
    props.setThreshold.low(low);
    props.setThreshold.medium(medium);
    props.setThreshold.high(high);
    props.setThreshold.critical(critical);
  };

  // Arrow keys nudge a marker by 0.1 (Shift: 0.5), using the same clamping as dragging
  const onHandleKey = (key: Level) => (e: KeyboardEvent<HTMLButtonElement>) => {
    const step = e.shiftKey ? 0.5 : 0.1;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault();
      props.applyThreshold(key, Math.round((t[key] - step) * 10) / 10);
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      props.applyThreshold(key, Math.round((t[key] + step) * 10) / 10);
    }
  };

  // Click on the axis snaps the nearest marker (within 0.3) to that value — unchanged behaviour
  const onAxisClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (!props.containerRef) return;
    const value = props.valueFromPosition(e.clientX, props.containerRef);
    const candidates: { type: Level; value: number }[] = [
      { type: 'low', value: t.low },
      { type: 'medium', value: t.medium },
      { type: 'high', value: t.high },
    ];
    let closest: Level | null = null;
    let minDistance = Infinity;
    candidates.forEach(({ type, value: thresholdValue }) => {
      const distance = Math.abs(value - thresholdValue);
      if (distance < 0.3 && distance < minDistance) {
        minDistance = distance;
        closest = type;
      }
    });
    if (!closest) return;
    const newValue = Math.max(0, Math.min(10, value));
    switch (closest) {
      case 'low':
        if (newValue < t.medium - 0.1) props.setThreshold.low(newValue);
        break;
      case 'medium':
        if (newValue > t.low + 0.1 && newValue < t.high - 0.1) props.setThreshold.medium(newValue);
        break;
      case 'high':
        if (newValue > t.medium + 0.1 && newValue < t.critical - 0.1) props.setThreshold.high(newValue);
        break;
    }
  };

  const onAxisMove = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (!props.containerRef) return;
    const value = props.valueFromPosition(e.clientX, props.containerRef);
    const tooltip = document.getElementById('scale-tooltip');
    if (tooltip) {
      tooltip.style.opacity = '1';
      tooltip.style.left = pct(value);
      tooltip.textContent = translate('settings.cvss.clickToSet', { value: value.toFixed(1) });
    }
  };

  const onAxisLeave = () => {
    const tooltip = document.getElementById('scale-tooltip');
    if (tooltip) tooltip.style.opacity = '0';
  };

  return (
    <div className="cvss">
      <div className="cvss-hd">
        <div className="cvss-hd-text">
          <h2 className="bd-h2">{translate('settings.cvss.title')}</h2>
          <span className="bd-body">
            {translate('settings.cvss.description')}
          </span>
        </div>
        <div className="cvss-hd-chips">
          <span className="bd-chip">
            {translate('settings.cvss.rangeChip')} <span className="bd-mono">0.0 – 10.0</span>
          </span>
          <span className="bd-chip mint">
            {translate('settings.cvss.coverage')} <span className="bd-mono">{props.coverage}%</span>
          </span>
        </div>
      </div>

      {/* Scale */}
      <div className="cvss-scale-card">
        <div className="cvss-scale-hd">
          <span className={`cvss-hint ${props.dragging ? 'live' : ''}`}>
            <span className="cvss-hint-dot" />
            {props.dragging ? translate('settings.cvss.hintDragging') : translate('settings.cvss.hintIdle')}
          </span>
          <div className="cvss-presets">
            {presets.map(({ id, label, icon: Icon, values }) => (
              <button key={id} type="button" className="bd-btn ghost cvss-preset" onClick={() => applyPreset(values)}>
                <Icon size={14} />
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="cvss-scale" ref={props.setContainerRef} onMouseLeave={() => props.dragging && props.onDragEnd()}>
          {/* Severity bands */}
          <div className="cvss-bar">
            {LEVELS.map(({ key }) => {
              const [start, end] = bands[key];
              if (end <= start) return null;
              const name = levelName(key);
              return (
                <div
                  key={key}
                  className={`cvss-band ${key} ${enabled[key] ? '' : 'off'}`}
                  style={{ left: pct(start), width: pct(end - start) }}
                  title={translate(enabled[key] ? 'settings.cvss.bandTitle' : 'settings.cvss.bandTitleOff', {
                    name,
                    start: start.toFixed(1),
                    end: end.toFixed(1),
                  })}
                >
                  <span className="cvss-band-label">
                    {name}
                    {!enabled[key] && <span className="cvss-band-off">{translate('settings.cvss.bandOff')}</span>}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Draggable markers */}
          <div className="cvss-handles">
            {LEVELS.filter((level) => level.key !== 'critical').map(({ key }) => (
              <button
                key={key}
                type="button"
                role="slider"
                aria-label={translate('settings.cvss.handleAriaLabel', { name: levelName(key) })}
                aria-valuemin={0}
                aria-valuemax={10}
                aria-valuenow={Number(t[key].toFixed(1))}
                aria-valuetext={t[key].toFixed(1)}
                className={`cvss-handle ${key} ${props.dragging === key ? 'dragging' : ''} ${enabled[key] ? '' : 'off'}`}
                style={{ left: pct(t[key]) }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  props.onDragStart(key, e.clientX);
                }}
                onTouchStart={(e) => {
                  if (e.touches[0]) props.onDragStart(key, e.touches[0].clientX);
                }}
                onKeyDown={onHandleKey(key)}
              >
                <span className="cvss-handle-knob" />
                <span className="cvss-handle-value bd-mono">{t[key].toFixed(1)}</span>
              </button>
            ))}
          </div>

          {/* Axis — click near a marker to snap it */}
          <div className="cvss-axis" onClick={onAxisClick} onMouseMove={onAxisMove} onMouseLeave={onAxisLeave}>
            {Array.from({ length: 11 }).map((_, score) => (
              <span key={score} className={`cvss-tick ${score % 2 === 0 ? 'major' : ''}`} style={{ left: pct(score) }}>
                <i />
                <span className="bd-mono">{score}</span>
              </span>
            ))}
            <div id="scale-tooltip" className="cvss-tooltip">
              <MousePointerClick size={12} /> {translate('settings.cvss.clickToSetThreshold')}
            </div>
          </div>
        </div>
      </div>

      {/* Severity cards */}
      <div className="cvss-levels">
        {LEVELS.map(({ key }) => {
          const [start, end] = bands[key];
          const on = enabled[key];
          const name = levelName(key);
          return (
            <div key={key} className={`cvss-level ${key} ${on ? 'on' : ''}`}>
              <div className="cvss-level-hd">
                <span className="cvss-level-badge">{translate(`settings.cvss.levels.${key}.badge`)}</span>
                <div className="cvss-level-text">
                  <h4 className="bd-h3">{translate(`settings.cvss.levels.${key}.title`)}</h4>
                  <span className="bd-meta">{translate(`settings.cvss.levels.${key}.description`)}</span>
                </div>
              </div>
              <div className="cvss-level-ft">
                <span className="cvss-level-range bd-mono">
                  {start.toFixed(1)} – {end.toFixed(1)}
                </span>
                <span className={`bd-chip ${on ? 'mint' : ''}`}>{on ? translate('settings.cvss.active') : translate('settings.cvss.inactive')}</span>
                <label className="cvss-level-switch">
                  <span className="bd-meta">{translate('settings.cvss.alerts')}</span>
                  <Switch
                    checked={on}
                    onChange={() => props.setEnabled[key](!on)}
                    onColor={switchColor[key]}
                    offColor={props.theme === 'dark' ? '#35545c' : '#cfdcdc'}
                    checkedIcon={false}
                    uncheckedIcon={false}
                    height={22}
                    width={42}
                    handleDiameter={18}
                    aria-label={translate('settings.cvss.alertsAriaLabel', { name })}
                  />
                </label>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="cvss-summary">
        <div className="cvss-summary-stat">
          <span className="bd-field-label">{translate('settings.cvss.coverage')}</span>
          <span className="bd-stat-num cvss-summary-num">{props.coverage}%</span>
          <div className="bd-progress">
            <i style={{ width: `${Math.min(100, Number(props.coverage))}%` }} />
          </div>
        </div>
        <div className="cvss-summary-stat">
          <span className="bd-field-label">{translate('settings.cvss.activeLevels')}</span>
          <span className="bd-stat-num cvss-summary-num">
            {activeLevels}
            <span className="cvss-summary-of">/ 4</span>
          </span>
        </div>
        <div className={`cvss-validity ${wellOrdered ? 'ok' : 'warn'}`}>
          {wellOrdered ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
          {wellOrdered ? translate('settings.cvss.validOk') : translate('settings.cvss.validWarn')}
        </div>
      </div>

      {/* Actions */}
      <div className="cvss-actions">
        <button type="button" className="bd-btn ghost" onClick={props.onReset}>
          <RotateCcw size={15} strokeWidth={2.2} />
          {translate('settings.cvss.reset')}
        </button>
        <button type="button" className="bd-btn secondary" onClick={props.onDiscard}>
          <X size={16} strokeWidth={2.2} />
          {translate('settings.cvss.discard')}
        </button>
        <button type="button" className="bd-btn primary" onClick={props.onApply}>
          <Check size={16} strokeWidth={2.4} />
          {translate('settings.cvss.apply')}
        </button>
      </div>
    </div>
  );
};
